import { useEffect, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import {
  fetchPostFinapiProviderAuthorizationUrl,
  fetchPostTruelayerBankAuthenticationUrl,
  fetchPostTruelayerProviderAuthorizationUrl,
} from '@iwoca/lapi-client/edge';
import { Modal } from '@iwoca/orion';
import { useLingui } from '@lingui/react/macro';

import BankConnectionView from './BankConnectionView';
import BankListView from './BankListView';
import styles from './BankSelectionModal.module.css';
import { useIsInDEOpenBankingCopyTest } from '../../../../hooks/useIsInDEOpenBankingCopyTest';
import { useLocale } from '../../../../hooks/useLocale';
import { TOnlineBank } from '../OpenBanking.types';

enum onlineBankSelectionModal {
  NONE = 'none',
  BANK_LIST = 'bankList',
  BANK_CONNECTION = 'bankConnection',
}

export const BankSelectionModal = ({
  isOpen,
  handleClose,
  bankList,
  shouldSkipPermissionView,
  stateKey,
  ableToShareLink,
}: {
  isOpen: boolean;
  handleClose: () => void;
  bankList: TOnlineBank[];
  shouldSkipPermissionView: boolean;
  stateKey: string;
  ableToShareLink: boolean;
}) => {
  const [banks, setBanks] = useState<TOnlineBank[]>(bankList);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBank, setSelectedBank] = useState<TOnlineBank | null>(null);
  const [view, setView] = useState<onlineBankSelectionModal>(
    onlineBankSelectionModal.BANK_LIST,
  );
  const [bankAuthURL, setBankAuthURL] = useState('');
  const [bankUrlError, setBankUrlError] = useState(false);

  const { t } = useLingui();
  const { isUK } = useLocale();
  const { isIncludedInOBCopyTest } = useIsInDEOpenBankingCopyTest();

  useEffect(() => {
    setBanks(bankList);
  }, [bankList]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);

    if (value === '') return setBanks(bankList);

    const filteredBanks = bankList.filter((bank) =>
      bank.bank_name.toLowerCase().includes(value.toLowerCase()),
    );

    setBanks(filteredBanks);
  };

  const handleBankSelect = async (bank: TOnlineBank) => {
    if (!stateKey) return;

    tracking.ampli.trueLayerBankSelection({
      bankId: bank.bank_id,
      bankName: bank.bank_name,
    });

    setSelectedBank(bank);
    setView(onlineBankSelectionModal.BANK_CONNECTION);

    try {
      let response = null;
      if (!bank.provider) {
        throw new Error();
      }

      if (isUK) {
        response = await fetchPostTruelayerBankAuthenticationUrl({
          stateKey,
          providerId: bank.bank_id,
        });

        if (response) {
          return setBankAuthURL(response.data.bank_url);
        }
      }

      if (bank.provider === 'truelayer') {
        response = await fetchPostTruelayerProviderAuthorizationUrl({
          stateKey,
          providerId: bank.bank_id,
        });
      } else if (bank.provider === 'finapi') {
        response = await fetchPostFinapiProviderAuthorizationUrl({
          stateKey,
          providerId: bank.bank_id,
        });
      }
      if (response) {
        setBankAuthURL(response?.data?.provider_authorization_url);
      }
    } catch {
      setBankUrlError(true);
    }
  };

  const handleBackClick = () => {
    setBankAuthURL('');
    setBankUrlError(false);
    setView(onlineBankSelectionModal.BANK_LIST);
  };

  const handleCloseAndReset = (closeCallback?: () => void) => {
    if (closeCallback) {
      closeCallback();
    }

    handleClose();
    setBankAuthURL('');
    setBankUrlError(false);
    setView(onlineBankSelectionModal.BANK_LIST);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => handleCloseAndReset()}
      closeIcon={true}
      headerBar={true}
      title={
        isIncludedInOBCopyTest
          ? t`Share business account statements`
          : t`Connect your business account`
      }
      className={`${styles.modalContainer} fs-mask dd-privacy-mask`}
    >
      {view === onlineBankSelectionModal.BANK_LIST && (
        <BankListView
          banks={banks}
          onSearch={handleSearch}
          searchTerm={searchTerm}
          onBankSelect={handleBankSelect}
          ableToShareLink={ableToShareLink}
        />
      )}
      {view === onlineBankSelectionModal.BANK_CONNECTION && selectedBank && (
        <BankConnectionView
          isOpen={isOpen}
          onClickBack={handleBackClick}
          handleCloseWithCallback={handleCloseAndReset}
          selectedBank={selectedBank}
          bankAuthURL={bankAuthURL}
          bankUrlError={bankUrlError}
          shouldSkipPermissionView={shouldSkipPermissionView}
        />
      )}
    </Modal>
  );
};
